/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import axios from 'axios';
import { settlementToken } from '@/utils/keywords';
export const useInvoiceTemplateStore = defineStore('invoiceTemplateStore', {
  state: () => ({
    servicesAndExpensesSummary: {},
    servicesAndExpenseByMatter: {},
    settlementStatementTemplate: {},
    servicesAndExpenseTable: [],
    invoiceTemplateLists: [],
    tableFields: [],
    serviceAndExpenseTableFields: [],
    customFields: [],
    alerts: {},
    invoiceInfo: {},
    expenseTableDataByMatterPage: []
  }),
  actions: {
    randomString(length) {
      return Math.round(Math.pow(36, length + 1) - Math.random() * Math.pow(36, length)).toString(36).slice(1);
    },
    async getTemplates() {
      try {
        const response = await axios({
          url: '/invoiceTemplates',
          method: 'GET'
        });
        this.$patch({
          invoiceTemplateLists: response.data
        });
        return response.data;
      } catch (error) {
        this.alerts.error(error.message || error.response?.data?.message);
      }
    },
    async getServicesAndExpensesSummaryTemplates(reload = false) {
      if (!reload && Object.keys(this.servicesAndExpensesSummary).length) return this.servicesAndExpensesSummary;
      try {
        const response = await axios({
          url: '/servicesAndExpensesSummaryTemplates',
          method: 'GET'
        });
        this.$patch({
          servicesAndExpensesSummary: response.data[0]
        });
        this.updateServicesAndExpensesTable(response.data[0]);
        return this.servicesAndExpensesSummary;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message || 'An unknown error occurred');
      }
    },
    async getServicesAndExpenseByMatter(reload = false) {
      if (!reload && Object.keys(this.servicesAndExpenseByMatter).length) return this.servicesAndExpenseByMatter;
      try {
        const response = await axios({
          url: '/service-and-expense-by-matter',
          method: 'GET'
        });
        response.data.serviceExpenseByMatterPage = true;
        this.$patch({
          servicesAndExpenseByMatter: response.data
        });
        this.updateServicesAndExpensesTable(response.data);
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
      }
    },
    async getSettlementStatementTemplate(reload = false) {
      // NOTE: HTML content from API response is stored as-is but will be sanitized
      // by consuming component (UpdateSettlementStatementTemplatePage.vue) before rendering.
      // This implements defense-in-depth: backend sanitizes on storage, frontend re-sanitizes on load.
      // Allowed HTML tags: p, strong, em, ul, li, br only. All attributes are stripped.
      if (!reload && Object.keys(this.settlementStatementTemplate).length) {
        return this.settlementStatementTemplate;
      }
      try {
        const response = await axios({
          url: '/settlement-statement-template',
          method: 'GET'
        });
        const template = {
          id: response.data?.settlementStatementTemplateId || 'settlement-statement-template',
          invoiceTemplateId: 'settlement-statement-template',
          name: 'Settlement Statement',
          settlementStatementPage: true,
          enableOpeningParagraph: response.data?.enableOpeningParagraph ?? true,
          openingParagraph: response.data?.openingParagraphHtml || '<p></p>',
          enableClosingParagraph: response.data?.enableClosingParagraph ?? true,
          closingParagraph: response.data?.closingParagraphHtml || '<p></p>',
          jurisdictionNote: response.data?.jurisdictionNote || '',
          updatedAt: response.data?.modifiedDate
        };
        this.$patch({
          settlementStatementTemplate: template
        });
        this.updateServicesAndExpensesTable(template);
        return template;
      } catch (error) {
        const defaultTemplate = {
          id: 'settlement-statement-template',
          invoiceTemplateId: 'settlement-statement-template',
          name: 'Settlement Statement',
          settlementStatementPage: true,
          enableOpeningParagraph: true,
          openingParagraph: `Dear ${settlementToken('client_name')},\n\nThis statement summarizes the disbursement of funds from the settlement of ${settlementToken('matter_name')}. The total gross recovery is ${settlementToken('gross_recovery')}. Of this amount, our firm's contingent attorney fee is ${settlementToken('attorney_fee')}, and total expenses advanced on your behalf are ${settlementToken('total_expenses')}.`,
          enableClosingParagraph: true,
          closingParagraph: `After payment of fees, expenses, and the liens listed above (${settlementToken('total_liens')} in total), your net recovery is ${settlementToken('net_to_client')}.\n\nBy signing below, you acknowledge that the fee and expenses shown are consistent with the contingent fee agreement you signed with our firm.`,
          jurisdictionNote: 'Intended for use on Idaho personal-injury matters. Firm is responsible for state-bar compliance of disclosure language.'
        };
        this.$patch({
          settlementStatementTemplate: defaultTemplate
        });
        this.updateServicesAndExpensesTable(defaultTemplate);
        return defaultTemplate;
      }
    },
    async updateSettlementStatementTemplate(template) {
      const updatedTemplate = {
        ...this.settlementStatementTemplate,
        ...template
      };
      try {
        const response = await axios({
          url: '/settlement-statement-template',
          method: 'POST',
          data: {
            enableOpeningParagraph: updatedTemplate.enableOpeningParagraph,
            openingParagraphHtml: updatedTemplate.openingParagraph,
            enableClosingParagraph: updatedTemplate.enableClosingParagraph,
            closingParagraphHtml: updatedTemplate.closingParagraph,
            jurisdictionNote: updatedTemplate.jurisdictionNote
          }
        });
        updatedTemplate.id = response.data?.settlementStatementTemplateId || updatedTemplate.id;
        updatedTemplate.updatedAt = response.data?.modifiedDate;
        this.$patch({
          settlementStatementTemplate: updatedTemplate
        });
        this.updateServicesAndExpensesTable(updatedTemplate);
        return true;
      } catch (error) {
        throw error;
      }
    },
    async updateServiceExpenseByMatterTableFields(tableFieldSettings, multiInvoiceTableFieldSettings) {
      try {
        await axios({
          url: '/service-and-expense-by-matter',
          method: 'PUT',
          data: {
            ...this.servicesAndExpenseByMatter,
            tableFieldSettings,
            multiInvoiceTableFieldSettings
          }
        });
        this.$patch({
          servicesAndExpenseByMatter: {}
        });
        return true;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
        return false;
      }
    },
    updateServicesAndExpensesTable(data) {
      const index = this.servicesAndExpenseTable.findIndex(item => item.id === data.id);
      if (index !== -1) {
        // Keep item in its original position
        this.servicesAndExpenseTable.splice(index, 1, data);
      } else {
        // If item doesn't exist, add it to the end
        this.servicesAndExpenseTable.push(data);
      }
    },
    async updateServicesAndExpensesSummaryTemplates(id, template) {
      const templateData = {
        closingParagraph: template.closingParagraph,
        customFieldSettings: template.customFieldSettings,
        enableClosingParagraph: template.enableClosingParagraph,
        enableCustomFields: template.enableCustomFields,
        enableOpeningParagraph: template.enableOpeningParagraph,
        enableSegmentByPracticeArea: template.enableSegmentByPracticeArea,
        enableSegmentByType: template.enableSegmentByType,
        openingParagraph: template.openingParagraph
      };
      try {
        await axios({
          url: `/servicesAndExpensesSummaryTemplates/${id}`,
          method: 'PUT',
          data: templateData
        });
        return true;
      } catch (error) {
        this.alerts.error(error.message || error.response?.data?.message);
        return false;
      }
    },
    async getTemplatesById(templateId) {
      try {
        const response = await axios({
          url: `/invoiceTemplates/${templateId}`,
          method: 'GET'
        });
        return response.data;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
      }
    },
    async getTableFields() {
      if (Object.keys(this.tableFields).length) {
        return this.tableFields;
      }
      try {
        const response = await axios({
          url: '/invoiceTemplates/table-fields',
          method: 'GET'
        });
        this.$patch({
          tableFields: response.data
        });
        return response.data;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
      }
    },
    async getServiceAndExpenseTableFields() {
      if (Object.keys(this.serviceAndExpenseTableFields).length) {
        return this.serviceAndExpenseTableFields;
      }
      try {
        const response = await axios({
          url: 'service-and-expense-by-matter/table-fields',
          method: 'GET'
        });
        this.$patch({
          serviceAndExpenseTableFields: response.data.map(({
            id,
            ...field
          }) => ({
            ...field,
            tableFieldId: id,
            tableFieldType: 'tableField'
          }))
        });
        return response.data;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
      }
    },
    async getCustomFields() {
      try {
        const response = await axios({
          url: `/invoicetemplates/customFields`,
          method: 'GET'
        });
        const customFields = response.data.filter(field => field.active).map(field => ({
          ...field,
          active: false,
          customFieldId: field.accountingId,
          tableFieldType: 'CustomField',
          displayName: field.name,
          fieldName: field.name,
          value: this.randomString(10)
        }));
        this.$patch({
          customFields
        });
        return customFields;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
      }
    },
    async deleteTemplate(templateId) {
      try {
        await axios({
          method: 'DELETE',
          url: `/invoiceTemplates/${templateId}`
        });
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
      }
    },
    async duplicateTemplate(templateId) {
      try {
        const response = await axios({
          method: 'POST',
          url: `/invoiceTemplates/${templateId}/duplicate`
        });
        return response.data;
      } catch (error) {
        this.alerts?.error(error.response?.data?.message || error.message || 'An unknown error occurred');
      }
    },
    async setDefaultTemplate(templateId) {
      try {
        await axios({
          method: 'PUT',
          url: `/invoiceTemplates/${templateId}`,
          data: {
            default: true
          }
        });
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
      }
    },
    async getInvoiceTemplateInfo() {
      if (Object.keys(this.invoiceInfo).length) {
        return this.invoiceInfo;
      }
      try {
        const response = await axios({
          method: 'GET',
          url: `/invoicetemplates/invoiceInfo`
        });
        this.$patch({
          invoiceInfo: response.data
        });
        return response.data;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
      }
    },
    async updateInvoiceTemplate(templateId, pdfState) {
      if (!pdfState.name) {
        this.alerts?.error('Template Name is required');
        return false;
      }
      const data = this.prepareInvoiceTemplateData(pdfState);
      try {
        await axios({
          method: 'PUT',
          url: `/invoiceTemplates/${templateId}`,
          data
        });
        return true;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
        return false;
      }
    },
    async createInvoiceTemplate(pdfState) {
      if (!pdfState.name) {
        this.alerts?.error('Template Name is required');
        return false;
      }
      const data = this.prepareInvoiceTemplateData(pdfState);
      try {
        const response = await axios({
          method: 'POST',
          url: `/invoiceTemplates/`,
          data
        });
        return response.data;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
        return false;
      }
    },
    prepareInvoiceTemplateData(pdfState) {
      return {
        name: pdfState.name,
        accountSummary: pdfState.accountSummary,
        hourSummary: pdfState.hourSummary,
        ledesSummary: pdfState.ledesSummary,
        showExpenseAmountsOnly: pdfState.showExpenseAmountsOnly,
        hideFixedFeeDetail: pdfState.hideFixedFeeDetail,
        enableSubTotal: pdfState.enableSubTotal,
        trustSummary: pdfState.trustSummary,
        tableFieldSettings: pdfState.tableFields,
        displayTrustSummaryText: pdfState.displayTrustSummaryText,
        memo: pdfState.memo,
        enableInvoiceDescription: pdfState.enableInvoiceDescription,
        invoicePdfDescription: pdfState.invoicePdfDescription,
        customFieldSettings: pdfState.customFieldSettings,
        enableCustomFields: pdfState.enableCustomFields,
        enableServiceExpensePage: pdfState.enableServiceExpensePage,
        enablePaidStamp: pdfState.enablePaidStamp,
        enableDueDate: pdfState.enableDueDate,
        enableMatterName: pdfState.enableMatterName,
        enablePageNumbers: pdfState.enablePageNumbers,
        enableServiceExpenseByMatterPage: pdfState.enableServiceExpenseByMatterPage,
        matterCustomFieldSettings: pdfState.matterCustomFieldSettings,
        enableMatterCustomFields: pdfState.enableMatterCustomFields,
        enableMatterHourSummary: pdfState.enableMatterHourSummary,
        enableMatterLedesSummary: pdfState.enableMatterLedesSummary,
        showCustomFieldsBelowMatterName: pdfState.showCustomFieldsBelowMatterName ?? false,
        labelSettings: pdfState.labelSettings || {},
        groupingEnabled: pdfState.groupingEnabled ?? false
      };
    },
    invoiceData(showExpenseAmountsOnly = false, enableMatterName = false, hideFixedFeeDetail = false) {
      const invoices = [{
        pdfPage: 1,
        Matter: 'Matter 1',
        subHeader: `Services${enableMatterName ? '' : ': ABC'}`,
        lineItems: [{
          UserInitials: 'ML',
          UserName: 'Matt Larsen',
          InvoiceDate: '2/12/22',
          BilledHours: 1,
          Amount: showExpenseAmountsOnly ? null : 425,
          WriteDownHours: 0,
          Rate: 425,
          Description: 'Lorem ipsum dolor sit amet',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 113
        }, {
          UserInitials: 'ML',
          UserName: 'Matt Larsen',
          InvoiceDate: '2/12/22',
          BilledHours: 0.69,
          Amount: showExpenseAmountsOnly ? null : 297.49,
          WriteDownHours: 0,
          Rate: 425,
          Description: 'Lorem ipsum dolor sit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 113
        }, {
          UserInitials: 'ML',
          UserName: 'Matt Larsen',
          InvoiceDate: '2/12/22',
          BilledHours: 2.89,
          Amount: showExpenseAmountsOnly ? null : 100,
          WriteDownHours: 0,
          Rate: 1232.49,
          Description: 'consectetur adipiscing elit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 113
        }]
      }, {
        pdfPage: 1,
        Matter: 'Matter 2',
        subHeader: `Services${enableMatterName ? '' : ': XYZ'}`,
        lineItems: [{
          UserInitials: 'PP',
          UserName: 'Paula Peterson',
          InvoiceDate: '2/11/23',
          BilledHours: 12.2,
          Amount: showExpenseAmountsOnly ? null : 100,
          WriteDownHours: 0,
          Rate: 200,
          Description: 'Lorem ipsum adipiscing elit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 112
        }, {
          UserInitials: 'PP',
          UserName: 'Paula Peterson',
          InvoiceDate: '2/11/23',
          BilledHours: 12.2,
          Amount: showExpenseAmountsOnly ? null : 100,
          WriteDownHours: 0,
          Rate: 200,
          Description: 'Consectetur adipiscing elit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 112
        }, {
          UserInitials: 'PP',
          UserName: 'Paula Peterson',
          InvoiceDate: '2/11/23',
          BilledHours: 12.2,
          Amount: showExpenseAmountsOnly ? null : 100,
          WriteDownHours: 0,
          Rate: 200,
          Description: 'Lorem adipiscing elit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 112
        }]
      }, {
        pdfPage: 2,
        Matter: 'Matter 3',
        subHeader: `Services${enableMatterName ? '' : ': ABC'}`,
        lineItems: [{
          UserInitials: 'PP',
          UserName: 'Paula Peterson',
          InvoiceDate: '2/12/22',
          BilledHours: 12.2,
          Amount: showExpenseAmountsOnly ? null : 100,
          WriteDownHours: 0,
          Rate: 200,
          Description: 'Lorem ipsum adipiscing elit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 111
        }, {
          UserInitials: 'PP',
          UserName: 'Paula Peterson',
          InvoiceDate: '2/12/22',
          BilledHours: 12.2,
          Amount: showExpenseAmountsOnly ? null : 100,
          WriteDownHours: 0,
          Rate: 200,
          Description: 'Lorem adipiscing elit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 111
        }, {
          UserInitials: 'PP',
          UserName: 'Paula Peterson',
          InvoiceDate: '2/12/22',
          BilledHours: 12.2,
          Amount: showExpenseAmountsOnly ? null : 100,
          WriteDownHours: 0,
          Rate: 200,
          Description: 'Lorem adipiscing elit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 111
        }]
      }, {
        pdfPage: 2,
        Matter: 'Matter 3',
        subHeader: `Services${enableMatterName ? '' : ': ABC'}`,
        lineItems: [{
          UserInitials: 'ML',
          UserName: 'Matt Larsen',
          InvoiceDate: '22/1/24',
          BilledHours: 12.2,
          Amount: showExpenseAmountsOnly ? null : 100,
          WriteDownHours: 0,
          Rate: 200,
          Description: 'Adipiscing elit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 101
        }, {
          UserInitials: 'ML',
          UserName: 'Matt Larsen',
          InvoiceDate: '22/1/24',
          BilledHours: 12.2,
          Amount: showExpenseAmountsOnly ? null : 100,
          WriteDownHours: 0,
          Rate: 200,
          Description: 'Lorem ipsum elit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 101
        }, {
          UserInitials: 'ML',
          UserName: 'Matt Larsen',
          InvoiceDate: '22/1/24',
          BilledHours: 12.2,
          Amount: showExpenseAmountsOnly ? null : 100,
          WriteDownHours: 0,
          Rate: 200,
          Description: 'Lorem adipiscing elit',
          LedesCode: 'L100, A101',
          LedesCodeDescription: 'Case Assessment, Research',
          ClientId: 100,
          MatterId: 101
        }]
      }];

      // Add Fixed Fee Summary section (always shown)
      const fixedFeeSummary = {
        pdfPage: 3,
        Matter: 'Fixed Fee Matter',
        subHeader: `Fixed Fee${enableMatterName ? '' : ': ABC'}`,
        isFixedFee: true,
        lineItems: [{
          UserInitials: '',
          UserName: '',
          InvoiceDate: '3/1/23 - 3/4/23',
          BilledHours: 15.5,
          Amount: showExpenseAmountsOnly ? null : 2500,
          WriteDownHours: 0,
          Rate: 0,
          Description: 'Fixed Fee Services - Total',
          ClientId: 100,
          MatterId: 115,
          isFixedFee: true,
          isFixedFeeSummary: true
        }]
      };
      invoices.push(fixedFeeSummary);

      // Add Fixed Fee section with details (can be hidden by hideFixedFeeDetail toggle)
      if (!hideFixedFeeDetail) {
        const fixedFeeMatter = {
          pdfPage: 3,
          Matter: 'Fixed Fee Matter',
          subHeader: `Fixed Fee Detail${enableMatterName ? '' : ': ABC'}`,
          isFixedFee: true,
          lineItems: [{
            UserInitials: 'JD',
            UserName: 'John Doe',
            InvoiceDate: '3/1/23',
            BilledHours: 5.5,
            Amount: showExpenseAmountsOnly ? null : 0,
            WriteDownHours: 0,
            Rate: 0,
            Description: 'Initial consultation - Fixed Fee',
            LedesCode: 'L100, A101',
            LedesCodeDescription: 'Case Assessment, Research',
            ClientId: 100,
            MatterId: 115,
            isFixedFee: true
          }, {
            UserInitials: 'JD',
            UserName: 'John Doe',
            InvoiceDate: '3/2/23',
            BilledHours: 3.25,
            Amount: showExpenseAmountsOnly ? null : 0,
            WriteDownHours: 0,
            Rate: 0,
            Description: 'Document review - Fixed Fee',
            LedesCode: 'L100, A101',
            LedesCodeDescription: 'Case Assessment, Research',
            ClientId: 100,
            MatterId: 115,
            isFixedFee: true
          }, {
            UserInitials: 'SM',
            UserName: 'Sarah Miller',
            InvoiceDate: '3/3/23',
            BilledHours: 2.75,
            Amount: showExpenseAmountsOnly ? null : 0,
            WriteDownHours: 0,
            Rate: 0,
            Description: 'Contract preparation - Fixed Fee',
            LedesCode: 'L100, A101',
            LedesCodeDescription: 'Case Assessment, Research',
            ClientId: 100,
            MatterId: 115,
            isFixedFee: true
          }, {
            UserInitials: 'SM',
            UserName: 'Sarah Miller',
            InvoiceDate: '3/4/23',
            BilledHours: 4.0,
            Amount: showExpenseAmountsOnly ? null : 0,
            WriteDownHours: 0,
            Rate: 0,
            Description: 'Client meeting - Fixed Fee',
            LedesCode: 'L100, A101',
            LedesCodeDescription: 'Case Assessment, Research',
            ClientId: 100,
            MatterId: 115,
            isFixedFee: true
          }]
        };
        invoices.push(fixedFeeMatter);
      }
      const expense = this.expenseData(enableMatterName);
      invoices.push(expense);
      invoices.forEach(section => {
        const subTotal = {
          subHeader: section.subHeader,
          subTotal: 'Sub-total',
          InvoiceDate: '',
          Description: '',
          BilledHours: parseFloat(section.lineItems.reduce((sum, item) => sum + (item.BilledHours || 0), 0)).toFixed(2),
          WriteDownHours: parseFloat(section.lineItems.reduce((sum, item) => sum + (item.WriteDownHours || 0), 0)).toFixed(2),
          Amount: parseFloat(section.lineItems.reduce((sum, item) => sum + (item.Amount || 0), 0)).toFixed(2),
          lineItems: []
        };
        if (parseFloat(subTotal.Amount).toFixed(2) !== 0) {
          section.lineItems.push(subTotal);
        }
      });
      invoices.forEach(section => {
        section.lineItems.forEach(item => {
          if (parseFloat(item.Amount) === 0) {
            delete item.Amount;
          }
        });
      });
      return invoices;
    },
    expenseData(enableMatterName) {
      return {
        pdfPage: 1,
        Matter: 'Expense Matter',
        subHeader: `Expenses${enableMatterName ? '' : ': ABC'}`,
        subTotal: 'Sub-total',
        lineItems: [{
          UserInitials: 'XX',
          UserName: 'Expense User',
          InvoiceDate: '1/1/23',
          BilledHours: 0,
          Amount: 1000,
          WriteDownHours: 0,
          Rate: 0,
          Description: 'Expense description',
          ClientId: 200,
          MatterId: 200
        }, {
          UserInitials: 'XX',
          UserName: 'Expense User',
          InvoiceDate: '1/1/23',
          BilledHours: 0,
          Amount: 1000,
          WriteDownHours: 0,
          Rate: 0,
          Description: 'Expense description1',
          ClientId: 200,
          MatterId: 200
        }, {
          UserInitials: 'XX',
          UserName: 'Expense User',
          InvoiceDate: '1/1/23',
          BilledHours: 0,
          Amount: 1000,
          WriteDownHours: 0,
          Rate: 0,
          Description: 'Expense description2',
          ClientId: 200,
          MatterId: 200
        }]
      };
    },
    expenseDataByMatterPage() {
      this.$patch({
        expenseTableDataByMatterPage: [{
          InvoiceDate: '1/1/23',
          MatterName: 'Matter 1',
          ClientMatterIdAndName: '100 - Matter 1',
          ClientMatterId: '100',
          MatterId: '111',
          Expenses: 900,
          Services: 1000,
          Total: 1900,
          InvoiceNumber: '1001'
        }, {
          InvoiceDate: '1/2/23',
          MatterName: 'Matter 2',
          ClientMatterIdAndName: '101 - Matter 2',
          ClientMatterId: '101',
          MatterId: '112',
          Expenses: 800,
          Services: 900,
          Total: 1700,
          InvoiceNumber: '1002'
        }, {
          InvoiceDate: '1/3/23',
          MatterName: 'Matter 3',
          ClientMatterIdAndName: '102 - Matter 3',
          ClientMatterId: '102',
          MatterId: '113',
          Expenses: 700,
          Services: 800,
          Total: 1500,
          InvoiceNumber: '1003'
        }, {
          InvoiceDate: '1/6/23',
          MatterName: 'Matter 4',
          ClientMatterIdAndName: '104 - Matter 4',
          ClientMatterId: '104',
          MatterId: '114',
          Expenses: 600,
          Services: 700,
          Total: 1300,
          InvoiceNumber: '1004'
        }, {
          InvoiceDate: '1/7/23',
          MatterName: 'Matter 5',
          ClientMatterIdAndName: '105 - Matter 5',
          ClientMatterId: '105',
          MatterId: '115',
          Expenses: 500,
          Services: 600,
          Total: 1100,
          InvoiceNumber: '1005'
        }, {
          InvoiceDate: '1/8/23',
          MatterName: 'Matter 6',
          ClientMatterIdAndName: '106 - Matter 6',
          ClientMatterId: '106',
          MatterId: '116',
          Expenses: 400,
          Services: 500,
          Total: 900,
          InvoiceNumber: '1006'
        }, {
          InvoiceDate: '1/9/23',
          MatterName: 'Matter 7',
          ClientMatterIdAndName: '107 - Matter 7',
          ClientMatterId: '107',
          MatterId: '117',
          Expenses: 300,
          Services: 400,
          Total: 700,
          InvoiceNumber: '1007'
        }, {
          InvoiceDate: '1/10/23',
          MatterName: 'Matter 8',
          ClientMatterIdAndName: '108 - Matter 8',
          ClientMatterId: '108',
          MatterId: '118',
          Expenses: 200,
          Services: 300,
          Total: 500,
          InvoiceNumber: '1008'
        }, {
          InvoiceDate: '1/11/23',
          MatterName: 'Matter 9',
          ClientMatterIdAndName: '109 - Matter 9',
          ClientMatterId: '109',
          MatterId: '119',
          Expenses: 100,
          Services: 200,
          Total: 300,
          InvoiceNumber: '1009'
        }, {
          InvoiceDate: '1/12/23',
          MatterName: 'Matter 10',
          ClientMatterIdAndName: '110 - Matter 10',
          ClientMatterId: '110',
          MatterId: '120',
          Expenses: 50,
          Services: 150,
          Total: 200,
          InvoiceNumber: '1010'
        }, {
          InvoiceDate: '1/13/23',
          MatterName: 'Matter 11',
          ClientMatterIdAndName: '111 - Matter 11',
          ClientMatterId: '111',
          MatterId: '121',
          Expenses: 75,
          Services: 125,
          Total: 200,
          InvoiceNumber: '1011'
        }, {
          InvoiceDate: '1/14/23',
          MatterName: 'Matter 12',
          ClientMatterIdAndName: '112 - Matter 12',
          ClientMatterId: '112',
          MatterId: '122',
          Expenses: 60,
          Services: 140,
          Total: 200,
          InvoiceNumber: '1012'
        }, {
          InvoiceDate: '1/15/23',
          MatterName: 'Matter 13',
          ClientMatterIdAndName: '113 - Matter 13',
          ClientMatterId: '113',
          MatterId: '123',
          Expenses: 90,
          Services: 110,
          Total: 200,
          InvoiceNumber: '1013'
        }, {
          InvoiceDate: '1/16/23',
          MatterName: 'Matter 14',
          ClientMatterIdAndName: '114 - Matter 14',
          ClientMatterId: '114',
          MatterId: '124',
          Expenses: 80,
          Services: 120,
          Total: 200,
          InvoiceNumber: '1014'
        }, {
          InvoiceDate: '1/17/23',
          MatterName: 'Matter 15',
          ClientMatterIdAndName: '115 - Matter 15',
          ClientMatterId: '115',
          MatterId: '125',
          Expenses: 70,
          Services: 130,
          Total: 200,
          InvoiceNumber: '1015'
        }]
      });
    },
    async requestEliteUpgrade() {
      try {
        await axios({
          method: 'POST',
          url: '/invoiceTemplates/send-elite-upgrade-email'
        });
        return true;
      } catch (error) {
        this.alerts?.error(error.message || error.response?.data?.message);
        return false;
      }
    },
    addCustomFieldsData() {
      this.expenseTableDataByMatterPage = this.expenseTableDataByMatterPage.map(invoice => {
        // Add each custom field as a new property in the invoice
        this.customFields.forEach(field => {
          invoice[field.name] = field.value; // or any other property you want to assign
        });
        return {
          ...invoice
        };
      });
    },
    async getSettlementStatementKeywords(settlementId) {
      // Fetches supported keywords from backend for settlement statement template.
      // When settlementId is provided (matter page), the backend resolves each
      // keyword's real per-matter `value`; omit it on the firm template page so
      // the frontend supplies sample values instead.
      // NOTE: Keyword names/descriptions are backend-controlled but rendered safely via Vue text interpolation.
      try {
        const response = await axios({
          url: '/settlement-statement-template/keywords',
          method: 'GET',
          ...(settlementId ? {
            params: {
              settlementId
            }
          } : {})
        });
        return response.data || [];
      } catch (error) {
        return [];
      }
    }
  }
});